import type { GuideTuning } from "../api/nativeGuide.js";
import { useSettings, type GuideFeedConfig, type ProviderConfig } from "../settings/settingsStore.js";
import { clampShift, useGuideMap, type GuidePin } from "./guideMapStore.js";

/**
 * A playlist's extra guide sources (Phase 3 step 3, docs/build-guide-playlists-epg.md §5):
 * after the playlist's own guide, in order, each filling the channels the ones before it
 * have no programmes for. Free (owner, 2026-10-21), and the TV app's only: the website
 * keeps the one guide.
 */

/** Extra sources a playlist may have (owner, 2026-10-21). The TV keeps 6 guide files in all. */
export const MAX_EXTRA_FEEDS = 3;

/** A guide address: http or https, as the playlist's own guide field takes. */
export function isGuideAddress(value: string): boolean {
  return /^https?:\/\/\S+$/i.test(value.trim());
}

/** The addresses of a playlist's switched-on extra sources, in order. */
export function extraGuideUrls(sourceId: string): string[] {
  const provider = useSettings.getState().providers.find((p) => p.id === sourceId);
  return (provider?.guideFeeds ?? []).filter((feed) => feed.enabled).map((feed) => feed.url);
}

/** The list with a new source at the end; unchanged when it is full, invalid or already there. */
export function addFeed(feeds: readonly GuideFeedConfig[], url: string, id: string): GuideFeedConfig[] {
  const clean = url.trim();
  if (!isGuideAddress(clean) || feeds.length >= MAX_EXTRA_FEEDS || feeds.some((feed) => feed.url === clean)) return [...feeds];
  return [...feeds, { id, url: clean, enabled: true }];
}

/** The list with one source moved a place earlier (-1) or later (+1). */
export function moveFeed(feeds: readonly GuideFeedConfig[], id: string, by: -1 | 1): GuideFeedConfig[] {
  const list = [...feeds];
  const at = list.findIndex((feed) => feed.id === id);
  const to = at + by;
  if (at < 0 || to < 0 || to >= list.length) return list;
  [list[at], list[to]] = [list[to]!, list[at]!];
  return list;
}

/** The list with one source's shift changed. */
export function shiftFeed(feeds: readonly GuideFeedConfig[], id: string, minutes: number): GuideFeedConfig[] {
  const shift = clampShift(minutes);
  return feeds.map((feed) => {
    if (feed.id !== id) return feed;
    const { shiftMinutes: _old, ...rest } = feed;
    return shift ? { ...rest, shiftMinutes: shift } : rest;
  });
}

/** The list with one source switched on or off. */
export function toggleFeed(feeds: readonly GuideFeedConfig[], id: string): GuideFeedConfig[] {
  return feeds.map((feed) => (feed.id === id ? { ...feed, enabled: !feed.enabled } : feed));
}

/** The list without one source. */
export function removeFeed(feeds: readonly GuideFeedConfig[], id: string): GuideFeedConfig[] {
  return feeds.filter((feed) => feed.id !== id);
}

/** A source's name on screen: its address's host, which never holds a login. */
export function feedLabel(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/**
 * A playlist's guide fixes for the TV's guide (Phase 3 step 4): "Choose guide"
 * pins and channel shifts, and each source's shift. A pin to a source that is
 * switched off or gone is left out, so the channel goes back to its automatic
 * match. The same object while nothing changes (the tuned mappings are kept by
 * it), and null with nothing to fix.
 */
export function guideTuning(sourceId: string): GuideTuning | null {
  const provider = useSettings.getState().providers.find((p) => p.id === sourceId);
  const { pins, shifts } = useGuideMap.getState();
  const ownPins = pins[sourceId];
  const ownShifts = shifts[sourceId];
  const held = heldTunings.get(sourceId);
  if (held && held.provider === provider && held.pins === ownPins && held.shifts === ownShifts) return held.tuning;
  const tuning = buildTuning(provider, ownPins, ownShifts);
  heldTunings.set(sourceId, { provider, pins: ownPins, shifts: ownShifts, tuning });
  return tuning;
}

const heldTunings = new Map<
  string,
  { provider: ProviderConfig | undefined; pins: Record<string, GuidePin> | undefined; shifts: Record<string, number> | undefined; tuning: GuideTuning | null }
>();

export function buildTuning(
  provider: ProviderConfig | undefined,
  pins: Readonly<Record<string, GuidePin>> | undefined,
  shifts: Readonly<Record<string, number>> | undefined,
): GuideTuning | null {
  const feeds = (provider?.guideFeeds ?? []).filter((feed) => feed.enabled);
  const urlOf = new Map(feeds.map((feed) => [feed.id, feed.url]));
  const tunedPins: Record<string, { url: string | null; guideChannelId: string }> = {};
  for (const [name, pin] of Object.entries(pins ?? {})) {
    const url = pin.feed === "own" ? null : urlOf.get(pin.feed);
    if (url !== undefined) tunedPins[name] = { url, guideChannelId: pin.guideChannelId };
  }
  const feedShifts: Record<string, number> = {};
  for (const feed of feeds) if (feed.shiftMinutes) feedShifts[feed.url] = feed.shiftMinutes;
  const ownShift = provider?.guideShiftMinutes ?? 0;
  const channelShifts = shifts ?? {};
  if (Object.keys(tunedPins).length === 0 && Object.keys(channelShifts).length === 0 && Object.keys(feedShifts).length === 0 && !ownShift) {
    return null;
  }
  return { pins: tunedPins, shifts: channelShifts, ownShift, feedShifts };
}
